import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { ClassificationLevel } from '@/types/classification'

/**
 * Classification level configuration with colors and descriptions
 */
const levelConfig: Record<
  ClassificationLevel,
  { label: string; description: string; className: string }
> = {
  PUBLIC: {
    label: 'Public',
    description: 'Information that can be freely shared with the public',
    className: 'bg-[oklch(0.7_0.15_145)] text-white border-transparent',
  },
  INTERNAL: {
    label: 'Internal',
    description: 'Information for internal use within the organization',
    className: 'bg-[oklch(0.65_0.15_230)] text-white border-transparent',
  },
  CONFIDENTIAL: {
    label: 'Confidential',
    description: 'Sensitive information requiring protection',
    className: 'bg-[oklch(0.75_0.15_40)] text-black border-transparent',
  },
  RESTRICTED: {
    label: 'Restricted',
    description: 'Highly sensitive information with strict access controls',
    className: 'bg-[oklch(0.5_0.2_25)] text-white border-transparent',
  },
  ITAR: {
    label: 'ITAR',
    description: 'Defense-related technical data subject to ITAR regulations',
    className: 'bg-[oklch(0.4_0.2_25)] text-white border-transparent',
  },
  REGULATORY_COMPLIANCE: {
    label: 'Regulatory Compliance',
    description: 'Data subject to regulatory compliance frameworks such as SOX, PCI-DSS, FISMA, CCPA, ITAR, EAR, or CUI',
    className: 'bg-[oklch(0.55_0.2_265)] text-white border-transparent',
  },
  INTELLECTUAL_PROPERTY: {
    label: 'Intellectual Property',
    description: 'Proprietary information including trade secrets, patents, copyrights, source code, and CTI',
    className: 'bg-[oklch(0.5_0.22_295)] text-white border-transparent',
  },
  CONTRACTS_AGREEMENTS: {
    label: 'Contracts & Agreements',
    description: 'Contractual information including NDAs, SLAs, CRADAs, licensing terms, and partner data',
    className: 'bg-[oklch(0.55_0.18_215)] text-white border-transparent',
  },
  PRIVACY_CONSENT: {
    label: 'Privacy & Consent',
    description: 'Privacy-sensitive data including PII, HIPAA, GDPR, consent records, and data subject rights',
    className: 'bg-[oklch(0.7_0.18_65)] text-black border-transparent',
  },
}

interface ClassificationBadgeProps {
  level: ClassificationLevel
  customColor?: string
  showTooltip?: boolean
  className?: string
}

/**
 * ClassificationBadge displays a colored badge for data classification level
 *
 * Colors follow security level convention:
 * - PUBLIC: Green (can share freely)
 * - INTERNAL: Blue (internal only)
 * - CONFIDENTIAL: Orange/Amber (sensitive)
 * - RESTRICTED: Red (highly sensitive)
 * - ITAR: Dark Red (defense regulated)
 */
export function ClassificationBadge({
  level,
  customColor,
  showTooltip = true,
  className,
}: ClassificationBadgeProps) {
  const config = levelConfig[level]

  if (!config) {
    return null
  }

  const badgeStyle = customColor
    ? { backgroundColor: customColor, color: 'white', borderColor: 'transparent' }
    : undefined

  const badge = (
    <Badge
      className={cn(!customColor && config.className, className)}
      style={badgeStyle}
      data-testid={`classification-badge-${level.toLowerCase()}`}
    >
      {config.label}
    </Badge>
  )

  if (!showTooltip) {
    return badge
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <p>{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
